import type { WorkLog } from '@aegislab/shared';
import type {
  QuestCheckpoint,
  QuestWorkLogItem,
  DailyWorkLog,
  DateFilter,
  TodayWorkLog,
} from '../model/types';

/**
 * 레거시 업무일지 content는 마감 시점에 `${태스크로그}${FREE_MEMO_MARKER}${자유 메모}` 형태로
 * 저장됐다. 태스크로그 합성은 폐기됐지만(항목A, H-5b 카드로 일원화) 과거 제출분에는 여전히
 * 마커가 박혀 있어, 이력 렌더에 raw content를 넘기면 stale 태스크로그가 카드와 중복 노출된다.
 * 에디터(TodayMemo)와 지난 일지(WorklogHistoryReader)가 동일하게 자유 메모만 취하도록 단일화.
 */
const FREE_MEMO_MARKER = '\n\n## 자유 메모\n';

export function extractFreeMemo(content: string | undefined): string {
  if (!content) return '';
  const idx = content.indexOf(FREE_MEMO_MARKER);
  if (idx === -1) return content;
  return content.slice(idx + FREE_MEMO_MARKER.length);
}

/**
 * 지난 일지 메모를 고쳐 저장할 본문 — 옛 형식(태스크로그 + 마커 + 메모)이면 마커 앞부분을 그대로
 * 두고 메모 부분만 바꾼다. 메모만 보내면 옛 태스크로그가 지워진다(운영 10건, 2026-10-02 실측).
 */
export function replaceFreeMemo(content: string | undefined, memo: string): string {
  const idx = content?.indexOf(FREE_MEMO_MARKER) ?? -1;
  if (!content || idx === -1) return memo;
  return `${content.slice(0, idx)}${FREE_MEMO_MARKER}${memo}`;
}

export function getCurrentCheckpoint(checkpoints: QuestCheckpoint[]) {
  return checkpoints.find((cp) => !cp.completed) || null;
}

export function isWithinDateRange(dateStr: string, filter: DateFilter): boolean {
  if (filter === 'all') return true;
  // 'custom'의 범위 판정은 isWithinCustomRange 소관 — 여기로 들어오면 통과시켜
  // 프리셋 분기('month' 폴백)로 오판하지 않게 한다.
  if (filter === 'custom') return true;
  const date = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (filter === 'today') {
    return date.getTime() === today.getTime();
  }
  if (filter === 'week') {
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 6);
    return date >= weekAgo && date <= today;
  }
  const monthAgo = new Date(today);
  monthAgo.setDate(monthAgo.getDate() - 29);
  return date >= monthAgo && date <= today;
}

/**
 * '기간 지정' 임의 범위 필터(양끝 포함). YYYY-MM-DD는 사전순=시간순이라 문자열 비교로
 * 충분(타임존 무관). 빈 경계는 무제한 — 한쪽만 입력해도 동작한다.
 */
export function isWithinCustomRange(dateStr: string, start: string, end: string): boolean {
  if (start && dateStr < start) return false;
  if (end && dateStr > end) return false;
  return true;
}

export function getTodayDateString(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function groupQuestsByDate(quests: QuestWorkLogItem[]): DailyWorkLog[] {
  const grouped = new Map<string, QuestWorkLogItem[]>();

  for (const quest of quests) {
    const existing = grouped.get(quest.date);
    if (existing) {
      existing.push(quest);
    } else {
      grouped.set(quest.date, [quest]);
    }
  }

  return Array.from(grouped.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, dateQuests]) => ({
      date,
      status: 'draft' as const,
      memo: '',
      checkpointSummaries: dateQuests.map((q) => ({
        questId: q.id,
        questTitle: q.title,
        questRarity: q.rarity,
        questStatus: q.status,
        checkpoints: q.checkpoints.map((cp) => ({
          id: cp.id,
          title: cp.title,
          completed: cp.completed,
          tasks: [],
        })),
      })),
    }));
}

export function workLogToTodayWorkLog(workLog: WorkLog): TodayWorkLog {
  return {
    date: workLog.logDate,
    status: (workLog.status?.toLowerCase() ?? 'draft') as TodayWorkLog['status'],
    memo: workLog.content,
    submittedAt: workLog.submittedAt ?? undefined,
    revisionCount: workLog.revisionCount ?? 0,
  };
}

// SOL(N일째) 계산은 shared util로 단일화 — KST 정확성 + windowRegistry 중복 제거.
// 기존 import 경로(@/features/worklog) 유지를 위해 re-export.
export { solDayFrom, solAnchor } from '@/shared/lib/format';
